import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  Search,
  Plus,
  Check,
  X,
  RefreshCw,
  Calendar,
  User,
  ArrowRight,
  HelpCircle,
  Bell
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getMyLoans,
  getActiveLoans,
  createLoan,
  approveLoan,
  cancelLoan,
  forwardLoan
} from '../../../api/payroll';
import { useAuth } from '../../../contexts/AuthContext';
import { formatDateToISO } from '../../../utils/formatters';
import BlurredValue from '../../../components/common/BlurredValue';
import PeriodFilter from '../../../components/common/PeriodFilter';
import { useFiscalPeriod } from '../../../contexts/FiscalPeriodContext';

const WholesaleEmployeeLoansPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { periodRange, getDateRangeParams } = useFiscalPeriod();

  const roleLevels = {
    staff: 1,
    branch_staff: 1,
    branch_admin: 2,
    admin: 3,
    wholesale_admin: 3,
    retail_admin: 3,
    system_admin: 4,
    super_admin: 5
  };

  const userLevel = roleLevels[user?.role] || 1;
  const isAdmin = userLevel >= 2;

  const [activeTab, setActiveTab] = useState('my_loans');
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form State for Requesting
  const [formData, setFormData] = useState({
    loan_type: 'personal',
    principal_amount: '',
    description: '',
    monthly_deduction: '',
    start_date: formatDateToISO(new Date()),
    user_id: user?.id
  });

  // Form State for Granting (Approving)
  const [approveData, setApproveData] = useState({
    repayment_method: 'one_time',
    installments_count: '1',
    repayment_period: 'one_time_payroll_run',
    deduction_sequence: '1',
    deduction_type: 'automatic',
    deduction_reminder: false
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      const { startDate, endDate } = getDateRangeParams();
      if (activeTab === 'my_loans') {
        data = await getMyLoans();
      } else if (activeTab === 'active_loans' && isAdmin) {
        data = await getActiveLoans({ module: 'wholesale', startDate, endDate });
      }
      setLoans(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load loans');
      console.error(error);
      setLoans([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, isAdmin, getDateRangeParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData, periodRange]);

  const handleRequestLoan = async (e) => {
    e.preventDefault();
    if (!formData.principal_amount || !formData.monthly_deduction) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await createLoan({
        ...formData,
        module: 'wholesale',
        user_id: formData.user_id || user.id
      });
      toast.success('Loan requested successfully and routed to assignee');
      setShowModal(false);
      fetchData();
      setFormData({
        loan_type: 'personal',
        principal_amount: '',
        description: '',
        monthly_deduction: '',
        start_date: formatDateToISO(new Date()),
        user_id: user?.id
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to request loan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveSubmit = async (e) => {
    e.preventDefault();
    if (!showApproveModal) return;
    setSubmitting(true);
    try {
      await approveLoan(showApproveModal.id, approveData);
      toast.success('Loan request approved and granted successfully');
      setShowApproveModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve loan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForward = async (loanId) => {
    if (!confirm('Forward this loan request to the next user level assignee?')) return;
    try {
      await forwardLoan(loanId);
      toast.success('Loan request forwarded successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to forward loan request');
    }
  };

  const handleCancelLoan = async (loanId) => {
    if (!confirm('Are you sure you want to cancel this loan request?')) return;
    try {
      await cancelLoan(loanId);
      toast.success('Loan request cancelled successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel loan request');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      active: 'bg-green-100 text-green-700',
      completed: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return (
      <span
        className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${styles[status] || 'bg-gray-100'}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-[1700px] mx-auto pb-10 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/wholesale/payroll')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                Wholesale Employee Loans
              </h1>
              <p className="text-sm text-gray-500">
                Manage wholesale employee loans, repayment schedules, and approvals
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PeriodFilter storageKey="wholesale_employee_loans" />
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition"
            >
              <Plus className="w-4 h-4" />
              Request Loan
            </button>
            <button
              onClick={fetchData}
              className="p-2 border rounded-lg hover:bg-gray-50 bg-white transition"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs and filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-6 border-b">
            <button
              onClick={() => setActiveTab('my_loans')}
              className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'my_loans' ? 'border-b-2 border-accent-gold text-accent-gold' : 'text-gray-500 hover:text-gray-700'}`}
            >
              My Loans
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab('active_loans')}
                className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'active_loans' ? 'border-b-2 border-accent-gold text-accent-gold' : 'text-gray-500 hover:text-gray-700'}`}
              >
                All Loans (Administrative)
              </button>
            )}
          </div>
        </div>

        {/* Loans Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-accent-gold border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : Array.isArray(loans) && loans.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase">
                    <th className="px-6 py-3">Start Date</th>
                    {activeTab === 'active_loans' && <th className="px-6 py-3">Employee</th>}
                    <th className="px-6 py-3">Type</th>
                    <th className="px-6 py-3 text-right">Principal</th>
                    <th className="px-6 py-3 text-right">Balance</th>
                    <th className="px-6 py-3 text-right">Deduction</th>
                    <th className="px-6 py-3">Assignee</th>
                    <th className="px-6 py-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loans.map((loan) => {
                    return (
                      <tr
                        key={loan.id}
                        onClick={() => navigate(`/wholesale/payroll/loans/${loan.id}`)}
                        className="hover:bg-amber-50/40 cursor-pointer transition"
                      >
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(loan.start_date).toLocaleDateString()}
                        </td>
                        {activeTab === 'active_loans' && (
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-semibold text-primary-black text-sm">
                                {loan.full_name}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">{loan.email}</p>
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm text-primary-black">
                          <span className="capitalize">{loan.loan_type?.replace('_', ' ')}</span>
                          {loan.description && (
                            <p
                              className="text-xs text-gray-400 truncate max-w-[200px]"
                              title={loan.description}
                            >
                              {loan.description}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-primary-black">
                          <BlurredValue value={formatCurrency(loan.principal_amount)} />
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-blue-600">
                          <BlurredValue value={formatCurrency(loan.balance)} />
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-600">
                          <BlurredValue value={formatCurrency(loan.monthly_deduction)} />
                          {loan.repayment_method === 'installments'
                            ? ` (${loan.installments_count} periods)`
                            : ' (one-time)'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {loan.assigned_to_name || <span className="text-gray-400">None</span>}
                          {loan.forwarded_by_name && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              Fwd by {loan.forwarded_by_name}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">{getStatusBadge(loan.status)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-16 text-center text-gray-500">
              <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="font-semibold text-lg text-primary-black">No loans found</p>
              <p className="text-gray-400 text-sm mt-1">Submit a loan request to get started.</p>
            </div>
          )}
        </div>

        {/* Request Loan Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-100">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-semibold text-primary-black">Request Loan / Advance</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleRequestLoan} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Loan Type
                  </label>
                  <select
                    value={formData.loan_type}
                    onChange={(e) => setFormData({ ...formData, loan_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                  >
                    <option value="personal">Personal Loan</option>
                    <option value="salary_advance">Salary Advance</option>
                    <option value="emergency">Emergency Advance</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Requested Amount (NGN)
                  </label>
                  <input
                    type="number"
                    value={formData.principal_amount}
                    onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                    placeholder="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Proposed Monthly Deduction (NGN)
                  </label>
                  <input
                    type="number"
                    value={formData.monthly_deduction}
                    onChange={(e) =>
                      setFormData({ ...formData, monthly_deduction: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                    placeholder="0"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Suggested amount to deduct per payroll cycle
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Reason / Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                    rows="3"
                    placeholder="Provide details for review..."
                    required
                  ></textarea>
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex gap-2">
                  <HelpCircle className="w-5 h-5 text-blue-500 shrink-0" />
                  <p className="text-xs text-blue-700 leading-relaxed">
                    <strong>Automatic Routing</strong>: This request will automatically route to the
                    closest manager in your department/branch hierarchy for initial review.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark disabled:opacity-50 text-sm"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Grant (Approve Settings) Modal */}
        {showApproveModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg border border-gray-100">
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-primary-black">
                    Grant Loan: Repayment Structure
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Configure repayment rules for {showApproveModal.full_name}
                  </p>
                </div>
                <button
                  onClick={() => setShowApproveModal(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleApproveSubmit} className="p-6 space-y-5">
                {/* Info summary */}
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      Principal Requested
                    </span>
                    <p className="font-semibold text-primary-black mt-0.5">
                      {formatCurrency(showApproveModal.principal_amount)}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      Interest Rate
                    </span>
                    <p className="font-semibold text-primary-black mt-0.5">
                      {showApproveModal.interest_rate || '0'}%
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Repayment Method
                    </label>
                    <select
                      value={approveData.repayment_method}
                      onChange={(e) =>
                        setApproveData({
                          ...approveData,
                          repayment_method: e.target.value,
                          installments_count: e.target.value === 'one_time' ? '1' : '4'
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold text-sm"
                    >
                      <option value="one_time">One-time payment</option>
                      <option value="installments">Spread (Installments)</option>
                    </select>
                  </div>

                  {approveData.repayment_method === 'installments' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Number of Deductions
                      </label>
                      <input
                        type="number"
                        min="2"
                        max="24"
                        value={approveData.installments_count}
                        onChange={(e) =>
                          setApproveData({ ...approveData, installments_count: e.target.value })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold text-sm"
                        required
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Payment Period
                    </label>
                    <select
                      value={approveData.repayment_period}
                      onChange={(e) =>
                        setApproveData({ ...approveData, repayment_period: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold text-sm"
                    >
                      <option value="one_time_payroll_run">One-time (in next run)</option>
                      <option value="spread_across_months">Spread across months</option>
                    </select>
                  </div>

                  {approveData.repayment_period === 'spread_across_months' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Month Sequence Interval
                      </label>
                      <select
                        value={approveData.deduction_sequence}
                        onChange={(e) =>
                          setApproveData({ ...approveData, deduction_sequence: e.target.value })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold text-sm"
                      >
                        <option value="1">Every month (1m sequence)</option>
                        <option value="2">Every 2 months</option>
                        <option value="3">Every quarter (3m sequence)</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Deduction Type
                    </label>
                    <select
                      value={approveData.deduction_type}
                      onChange={(e) =>
                        setApproveData({ ...approveData, deduction_type: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold text-sm"
                    >
                      <option value="automatic">Automatic Payroll Deduction</option>
                      <option value="manual">Manual Direct Repayment</option>
                    </select>
                  </div>

                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={approveData.deduction_reminder}
                        onChange={(e) =>
                          setApproveData({ ...approveData, deduction_reminder: e.target.checked })
                        }
                        className="rounded border-gray-300 text-accent-gold focus:ring-accent-gold w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                        <Bell className="w-4 h-4 text-gray-400" />
                        Trigger deduction reminder
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-5 border-t">
                  <button
                    type="button"
                    onClick={() => setShowApproveModal(null)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm flex items-center gap-1.5"
                  >
                    <Check size={16} /> Grant Loan
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WholesaleEmployeeLoansPage;
